"use client";

import { useState } from "react";
import { MapPin } from "lucide-react";

type ProviderMapEmbedProps = {
  providerName: string;
  lat: number;
  lng: number;
  address: string;
};

/**
 * Lazy-loading OpenStreetMap embed via Nominatim tile layer.
 * No API key required. Only renders when lat/lng are available.
 *
 * The iframe is hidden behind a click-to-load button to avoid
 * loading tiles on every page view (performance + privacy).
 */
export function ProviderMapEmbed({
  providerName,
  lat,
  lng,
  address,
}: ProviderMapEmbedProps) {
  const [loaded, setLoaded] = useState(false);

  // OpenStreetMap embed URL: no key, no tracking pixel
  const zoom = 16;
  const osmEmbedUrl =
    `https://www.openstreetmap.org/export/embed.html` +
    `?bbox=${lng - 0.005},${lat - 0.003},${lng + 0.005},${lat + 0.003}` +
    `&layer=mapnik` +
    `&marker=${lat},${lng}`;

  return (
    <section
      className="mt-10 rounded-2xl border border-[var(--color-border)] bg-white p-6 shadow-sm"
      aria-label="Location map"
    >
      <div className="flex items-center gap-2">
        <MapPin size={15} className="text-[var(--color-accent)]" aria-hidden="true" />
        <p className="section-label-muted">Location</p>
      </div>
      {address && (
        <p className="mt-1 text-sm text-[var(--color-muted)]">{address}</p>
      )}

      <div className="mt-4 overflow-hidden rounded-xl border border-[var(--color-border)]">
        {loaded ? (
          <iframe
            src={osmEmbedUrl}
            width="100%"
            height="280"
            title={`Map showing location of ${providerName}`}
            loading="lazy"
            className="block w-full"
            style={{ border: 0 }}
            aria-label={`Map showing the location of ${providerName} at ${address || `${lat}, ${lng}`}`}
          />
        ) : (
          <button
            type="button"
            onClick={() => setLoaded(true)}
            className="flex h-[280px] w-full flex-col items-center justify-center gap-3 bg-surface-1 text-[var(--color-muted)] transition-colors hover:bg-[var(--color-surface-2)]"
            aria-label={`Load map for ${providerName}`}
          >
            <MapPin size={28} className="text-[var(--color-accent)] opacity-60" aria-hidden="true" />
            <span className="text-sm font-semibold text-[var(--color-secondary)]">
              Click to load map
            </span>
            <span className="text-xs text-[var(--color-muted)]">
              Opens OpenStreetMap, no tracking
            </span>
          </button>
        )}
      </div>

      <p className="mt-2 text-xs text-[var(--color-muted)]">
        Map data © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" className="underline hover:text-[var(--color-primary)]">OpenStreetMap</a> contributors
      </p>
    </section>
  );
}

// Server-side guard: only renders when coordinates are present.
export function ProviderMapEmbedIfAvailable({
  providerName,
  lat,
  lng,
  address,
}: {
  providerName: string;
  lat: number | null;
  lng: number | null;
  address: string;
}) {
  if (!lat || !lng) return null;
  return (
    <ProviderMapEmbed
      providerName={providerName}
      lat={lat}
      lng={lng}
      address={address}
    />
  );
}
